import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  type CSSProperties,
} from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, X, Plus, Star, ImageOff, Eye, Flag } from "lucide-react";
import { useAuth } from "@/features/authentication";
import { Button } from "@/components/ui/button";
import type { AdjustmentsData, AIModeState, RankedSpace } from "../types";
import { ReportProblemsModal } from "../components/ReportProblemsModal";
import {
  buildSpacePhotosByBuilding,
  resolveSpacePhotos,
  buildingKeyOf,
  groupRoomsByBuilding,
} from "../utils/resolveSpacePhotos";
import {
  buildPhotoItems,
  toCuratedUrls,
  type PhotoItem,
} from "../utils/imageCuration";

interface ImagesStepProps {
  data: AdjustmentsData | null;
  state: AIModeState;
  onNext: (data: AdjustmentsData) => void;
  onChange?: (data: AdjustmentsData) => void;
  onValidityChange?: (valid: boolean) => void;
  registerNext?: (fn: () => void) => void;
  previewSpaceId?: string | null;
  onPreview?: (spaceId: string) => void;
}

// A curation gallery is just an ordered PhotoItem[]. Room photos are keyed by
// room id; building ("Espaço") photos are keyed by building (shared by all
// rooms in the building) — see groupRoomsByBuilding / buildingKeyOf.
type GalleryMap = Record<string, PhotoItem[]>;

// Keep selected photos first (in their order), deselected after — so the
// "Na proposta" sequence and the "Disponíveis" pool stay consistent.
function normalize(items: PhotoItem[]): PhotoItem[] {
  return [
    ...items.filter((i) => i.selected),
    ...items.filter((i) => !i.selected),
  ];
}

// ── Pure gallery transforms (shared by the room and building galleries) ──────
function applySelect(
  items: PhotoItem[],
  url: string,
  selected: boolean,
): PhotoItem[] {
  return normalize(
    items.map((item) => (item.url === url ? { ...item, selected } : item)),
  );
}

function applyReorder(items: PhotoItem[], orderedUrls: string[]): PhotoItem[] {
  const byUrl = new Map(items.map((i) => [i.url, i]));
  const reorderedSelected = orderedUrls
    .map((u) => byUrl.get(u))
    .filter((i): i is PhotoItem => i != null);
  // Keep every item not covered by `orderedUrls` — that includes deselected
  // photos AND a still-selected photo that failed to load (so it's absent from
  // the visible/ordered list). Filtering by `!selected` alone would drop the
  // latter permanently on the first drag.
  const rest = items.filter((i) => !orderedUrls.includes(i.url));
  return [...reorderedSelected, ...rest];
}

function applyAddAll(items: PhotoItem[]): PhotoItem[] {
  return items.map((item) => ({ ...item, selected: true }));
}

// ─── A photo already in the proposal: draggable, removable, may be the cover ────
function ProposalThumb({
  item,
  accentColor,
  order,
  isCover,
  onRemove,
  onError,
}: {
  item: PhotoItem;
  accentColor: string;
  order: number;
  isCover: boolean;
  onRemove: () => void;
  onError: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.url });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
    boxShadow: isCover
      ? `0 0 0 2px ${accentColor}, 0 8px 20px rgba(26,24,20,0.12)`
      : "0 1px 3px rgba(26,24,20,0.10)",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative aspect-[16/9] rounded-xl overflow-hidden bg-[#F2F0EC]"
    >
      <img
        src={item.url}
        alt=""
        className="w-full h-full object-cover"
        onError={onError}
      />

      {/* Drag handle (always visible) */}
      <button
        type="button"
        aria-label="Reordenar foto"
        className="absolute top-1.5 left-1.5 w-6 h-6 rounded-md flex items-center justify-center cursor-grab active:cursor-grabbing touch-none"
        style={{ background: "rgba(255,255,255,0.92)" }}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-3.5 h-3.5" style={{ color: "#6B6661" }} />
      </button>

      {/* Remove */}
      <button
        type="button"
        aria-label="Remover da proposta"
        onClick={onRemove}
        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
        style={{ background: "rgba(26,24,20,0.72)" }}
      >
        <X className="w-3.5 h-3.5" style={{ color: "white" }} />
      </button>

      {/* Cover tag / order badge */}
      {isCover ? (
        <span
          className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wide"
          style={{ background: accentColor, color: "#1A1814" }}
        >
          <Star className="w-2.5 h-2.5" fill="#1A1814" />
          Capa
        </span>
      ) : (
        <span
          className="absolute bottom-1.5 left-1.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold tabular-nums"
          style={{ background: "rgba(255,255,255,0.92)", color: "#1A1814" }}
        >
          {order}
        </span>
      )}
    </div>
  );
}

// ─── A photo available to add ───────────────────────────────────────────────────
function AvailableThumb({
  item,
  onAdd,
  onError,
}: {
  item: PhotoItem;
  onAdd: () => void;
  onError: () => void;
}) {
  return (
    <button
      type="button"
      aria-label="Adicionar à proposta"
      onClick={onAdd}
      className="group relative aspect-[16/9] rounded-lg overflow-hidden bg-[#F2F0EC]"
      style={{ boxShadow: "inset 0 0 0 1px #E6E2DD" }}
    >
      <img
        src={item.url}
        alt=""
        className="w-full h-full object-cover transition-all duration-200"
        style={{ opacity: 0.5, filter: "grayscale(0.5)" }}
        onError={onError}
      />
      <span className="absolute inset-0 flex items-center justify-center">
        <span
          className="w-7 h-7 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
          style={{ background: "rgba(255,255,255,0.92)" }}
        >
          <Plus className="w-4 h-4" style={{ color: "#1A1814" }} />
        </span>
      </span>
    </button>
  );
}

// ─── One labeled gallery (Sala or Espaço) with its two zones ────────────────────
function GallerySection({
  label,
  subtitle,
  accentColor,
  items,
  onReorder,
  onRemove,
  onAdd,
  onAddAll,
  onError,
  testId,
}: {
  label: string;
  subtitle: string;
  accentColor: string;
  items: PhotoItem[];
  onReorder: (orderedUrls: string[]) => void;
  onRemove: (url: string) => void;
  onAdd: (url: string) => void;
  onAddAll: () => void;
  onError: (url: string) => void;
  testId?: string;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const proposal = items.filter((i) => i.selected);
  const available = items.filter((i) => !i.selected);

  if (items.length === 0) return null;

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const urls = proposal.map((i) => i.url);
      const oldIndex = urls.indexOf(String(active.id));
      const newIndex = urls.indexOf(String(over.id));
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(arrayMove(urls, oldIndex, newIndex));
      }
    }
  }

  return (
    <div data-testid={testId}>
      {/* Section header */}
      <div className="flex items-center gap-2.5 mb-3">
        <span
          className="flex-none w-2 h-2 rounded-full"
          style={{ background: accentColor, boxShadow: "0 0 0 3px #F4F1EC" }}
        />
        <div className="min-w-0 flex-1">
          <p
            className="text-[11px] font-bold uppercase tracking-[0.08em]"
            style={{ color: "#1A1814" }}
          >
            {label}
          </p>
          <p className="text-[11px] leading-tight" style={{ color: "#9E9A94" }}>
            {subtitle}
          </p>
        </div>
        <span className="text-[11px] tabular-nums" style={{ color: "#9E9A94" }}>
          {proposal.length} na proposta
        </span>
      </div>

      {/* Zone: in the proposal */}
      {proposal.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={proposal.map((i) => i.url)}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-3 gap-2.5">
              {proposal.map((item, idx) => (
                <ProposalThumb
                  key={item.url}
                  item={item}
                  accentColor={accentColor}
                  order={idx + 1}
                  isCover={idx === 0}
                  onRemove={() => onRemove(item.url)}
                  onError={() => onError(item.url)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div
          className="rounded-xl px-4 py-5 text-center text-xs"
          style={{ border: "1px dashed #D4CFC9", color: "#9E9A94" }}
        >
          Nenhuma foto nesta seção — adicione abaixo.
        </div>
      )}

      {/* Zone: available to add */}
      {available.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <span
              className="text-[10px] font-semibold uppercase tracking-wide"
              style={{ color: "#9E9A94" }}
            >
              Disponíveis ({available.length})
            </span>
            <button
              type="button"
              onClick={onAddAll}
              className="text-[11px] font-semibold"
              style={{ color: "#8C7200" }}
            >
              Adicionar todas
            </button>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {available.map((item) => (
              <AvailableThumb
                key={item.url}
                item={item}
                onAdd={() => onAdd(item.url)}
                onError={() => onError(item.url)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Step ──────────────────────────────────────────────────────────────────────
export default function ImagesStep({
  data,
  state,
  onNext,
  onChange,
  onValidityChange,
  registerNext,
  previewSpaceId,
  onPreview,
}: ImagesStepProps) {
  const selectedSpaces = useMemo<RankedSpace[]>(
    () => data?.selectedSpaces ?? [],
    [data?.selectedSpaces],
  );

  // Resolve building photos so a room missing its own can borrow from a sibling.
  const spacePhotosByBuilding = useMemo(
    () =>
      buildSpacePhotosByBuilding([
        ...(state.location?.rankedSpaces ?? []),
        ...(state.location?.extraSpaces ?? []),
        ...selectedSpaces,
      ]),
    [state.location?.rankedSpaces, state.location?.extraSpaces, selectedSpaces],
  );

  // Rooms grouped by building, in display order (one card per building).
  const buildingGroups = useMemo(
    () => groupRoomsByBuilding(selectedSpaces),
    [selectedSpaces],
  );

  // Room ("Sala") galleries, keyed by room id.
  const [roomCuration, setRoomCuration] = useState<GalleryMap>(() => {
    const map: GalleryMap = {};
    for (const space of selectedSpaces) {
      map[space.id] = buildPhotoItems(space.images ?? [], space.selectedImages);
    }
    return map;
  });

  // Building ("Espaço") galleries, keyed by building. Shared by all rooms in the
  // building, so editing here applies to every room on save — no sync dialog.
  const [buildingCuration, setBuildingCuration] = useState<GalleryMap>(() => {
    const map: GalleryMap = {};
    for (const group of buildingGroups) {
      const firstRoom = group.rooms[0];
      const curated = group.rooms.find(
        (r) => r.selectedSpacePhotos != null,
      )?.selectedSpacePhotos;
      map[group.key] = buildPhotoItems(
        resolveSpacePhotos(firstRoom, spacePhotosByBuilding),
        curated,
      );
    }
    return map;
  });

  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [reportOpen, setReportOpen] = useState(false);
  const { userInfo } = useAuth();

  const buildData = useCallback((): AdjustmentsData => {
    const curatedSpaces = selectedSpaces.map((space) => ({
      ...space,
      selectedImages: toCuratedUrls(roomCuration[space.id] ?? []),
      selectedSpacePhotos: toCuratedUrls(
        buildingCuration[buildingKeyOf(space)] ?? [],
      ),
    }));
    // Guard against `data` being null on back-navigation before AdjustmentsStep
    // has saved — spreading null would silently drop every other AdjustmentsData
    // field (creditsPrice, spaceAdjustments, contractMonths, …).
    return {
      ...(data ?? ({} as AdjustmentsData)),
      selectedSpaces: curatedSpaces,
    };
  }, [selectedSpaces, roomCuration, buildingCuration, data]);

  // Every room must keep at least one photo — its own room photo or one of its
  // building's shared space photos.
  const isValid = useMemo(
    () =>
      selectedSpaces.every((space) => {
        const room = roomCuration[space.id] ?? [];
        const building = buildingCuration[buildingKeyOf(space)] ?? [];
        return room.some((i) => i.selected) || building.some((i) => i.selected);
      }),
    [selectedSpaces, roomCuration, buildingCuration],
  );

  const buildDataRef = useRef(buildData);
  buildDataRef.current = buildData;

  useEffect(() => {
    registerNext?.(() => onNext(buildDataRef.current()));
  }, [registerNext, onNext]);

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  useEffect(() => {
    // Read through the ref so a parent-replaced `data`/`selectedSpaces` is
    // reflected even when only the curation maps trigger this effect.
    onChange?.(buildDataRef.current());
  }, [roomCuration, buildingCuration, onChange]);

  const updateGallery = useCallback(
    (
      setMap: React.Dispatch<React.SetStateAction<GalleryMap>>,
      key: string,
      transform: (items: PhotoItem[]) => PhotoItem[],
    ) => {
      setMap((prev) => {
        const items = prev[key];
        if (!items) return prev;
        return { ...prev, [key]: transform(items) };
      });
    },
    [],
  );

  const setRoomSelected = useCallback(
    (roomId: string, url: string, selected: boolean) =>
      updateGallery(setRoomCuration, roomId, (it) =>
        applySelect(it, url, selected),
      ),
    [updateGallery],
  );
  const reorderRoom = useCallback(
    (roomId: string, urls: string[]) =>
      updateGallery(setRoomCuration, roomId, (it) => applyReorder(it, urls)),
    [updateGallery],
  );
  const addAllRoom = useCallback(
    (roomId: string) => updateGallery(setRoomCuration, roomId, applyAddAll),
    [updateGallery],
  );

  const setBuildingSelected = useCallback(
    (key: string, url: string, selected: boolean) =>
      updateGallery(setBuildingCuration, key, (it) =>
        applySelect(it, url, selected),
      ),
    [updateGallery],
  );
  const reorderBuilding = useCallback(
    (key: string, urls: string[]) =>
      updateGallery(setBuildingCuration, key, (it) => applyReorder(it, urls)),
    [updateGallery],
  );
  const addAllBuilding = useCallback(
    (key: string) => updateGallery(setBuildingCuration, key, applyAddAll),
    [updateGallery],
  );

  const handleImageError = useCallback((url: string) => {
    setFailedImages((prev) => new Set(prev).add(url));
  }, []);

  const visibleItems = useCallback(
    (items: PhotoItem[]) => items.filter((i) => !failedImages.has(i.url)),
    [failedImages],
  );

  if (selectedSpaces.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-8">
        <ImageOff className="w-10 h-10" style={{ color: "#C8C4BF" }} />
        <p className="text-sm" style={{ color: "#6B6661" }}>
          Nenhum espaço selecionado para curar imagens.
        </p>
      </div>
    );
  }

  // One room inside a building card: its name, a preview button, and its "Sala"
  // gallery. "Sem fotos" when neither this room nor its building has a selection.
  const renderRoomSection = (room: RankedSpace) => {
    const roomItems = visibleItems(roomCuration[room.id] ?? []);
    const buildingItems = buildingCuration[buildingKeyOf(room)] ?? [];
    const isEmpty =
      !roomItems.some((i) => i.selected) &&
      !buildingItems.some((i) => i.selected);
    const isActivePreview =
      room.id === (previewSpaceId ?? selectedSpaces[0]?.id);
    return (
      <div
        key={room.id}
        data-testid={`room-section-${room.id}`}
        className="rounded-xl p-4"
        style={{ background: "#FBFAF8", boxShadow: "inset 0 0 0 1px #ECEAE6" }}
      >
        <div className="flex items-center justify-between gap-3 mb-3">
          <p
            className="text-sm font-semibold leading-tight flex items-center gap-2"
            style={{ color: "#1A1814" }}
          >
            {room.roomName}
            {isEmpty && (
              <span
                className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full"
                style={{ background: "#FBEAE8", color: "#C0392B" }}
              >
                Sem fotos
              </span>
            )}
          </p>
          <button
            type="button"
            onClick={() => onPreview?.(room.id)}
            className="flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors flex-none"
            style={
              isActivePreview
                ? { background: "#1A1814", color: "#FFFFFF" }
                : { background: "#F2F0EC", color: "#1A1814" }
            }
          >
            <Eye className="w-3.5 h-3.5" />
            Pré-visualizar
          </button>
        </div>
        <GallerySection
          label="Sala privativa"
          subtitle="o escritório em si"
          accentColor="#FFD900"
          items={roomItems}
          onReorder={(urls) => reorderRoom(room.id, urls)}
          onRemove={(url) => setRoomSelected(room.id, url, false)}
          onAdd={(url) => setRoomSelected(room.id, url, true)}
          onAddAll={() => addAllRoom(room.id)}
          onError={handleImageError}
        />
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-5 px-8 py-6">
      {/* Step hero — Agent-First. The right-hand actions slot is the mount
          point for a future AI image-curation assistant. */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-lg font-bold" style={{ color: "#1A1814" }}>
            Imagens da proposta
          </h2>
          <p
            className="text-sm mt-1 leading-relaxed"
            style={{ color: "#6B6661" }}
          >
            Selecione e organize as fotos que o cliente verá na proposta.{" "}
            <b>Sala</b> e <b>Espaço</b> são grupos separados — a primeira foto
            de cada grupo é usada como capa.
          </p>
        </div>
        {/* agent-slot: AI assistant entry point mounts here */}
        <Button
          type="button"
          variant="outline"
          className="gap-1.5 shrink-0"
          style={{ borderColor: "#E8E6E3", color: "#43403D" }}
          onClick={() => setReportOpen(true)}
        >
          <Flag className="w-4 h-4" />
          Relatar Problemas
        </Button>
      </div>

      {buildingGroups.map((group) => {
        const buildingItems = visibleItems(buildingCuration[group.key] ?? []);
        return (
          <div
            key={group.key}
            className="rounded-2xl p-5"
            style={{
              background: "#FFFFFF",
              boxShadow: "inset 0 0 0 1px #ECEAE6",
            }}
          >
            <div className="mb-4">
              <p
                className="text-sm font-bold leading-tight"
                style={{ color: "#1A1814" }}
              >
                {group.spaceName}
              </p>
              <p
                className="text-[11px] font-semibold uppercase tracking-[0.08em] mt-0.5"
                style={{ color: "#8C7200" }}
              >
                {group.rooms.length}{" "}
                {group.rooms.length === 1 ? "sala" : "salas"}
              </p>
            </div>

            {/* Office (room) cards first, then the building ("Espaço") photos. */}
            <div className="flex flex-col gap-4">
              {group.rooms.map(renderRoomSection)}
            </div>

            {buildingItems.length > 0 && (
              <div className="mt-5">
                <GallerySection
                  label="Espaço"
                  subtitle="áreas comuns do prédio"
                  accentColor="#C8C4BF"
                  testId={`espaco-gallery-${group.key}`}
                  items={buildingItems}
                  onReorder={(urls) => reorderBuilding(group.key, urls)}
                  onRemove={(url) => setBuildingSelected(group.key, url, false)}
                  onAdd={(url) => setBuildingSelected(group.key, url, true)}
                  onAddAll={() => addAllBuilding(group.key)}
                  onError={handleImageError}
                />
              </div>
            )}
          </div>
        );
      })}

      <ReportProblemsModal
        open={reportOpen}
        onOpenChange={setReportOpen}
        spaces={selectedSpaces}
        reporterEmail={userInfo?.profile?.email ?? null}
      />
    </div>
  );
}
